/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { spacing } from '../../../shared/theme/spacing';
import { typography } from '../../../shared/theme/typography';
import { colors } from '../../../shared/theme/colors';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { getOrganizationDisplayName } from '../../navigation/organizationDisplayName';
import { resolveOrganizationRubroDisplayLabel } from '../domain/businessProfile';
import { useBusinessProfileStore } from '../store/useBusinessProfileStore';
import { useOrganizationSwitcherAccess } from '../hooks/useOrganizationSwitcherAccess';
import { useModulePermission } from '../hooks/useModulePermission';
import { OrganizationSwitcherSheet } from './OrganizationSwitcherSheet';
import {
  navigateToOrganizationIdentity,
  type OrganizationIdentityNavigationContext,
} from '../../../settings/navigation/openOrganizationIdentity';

type Props = {
  navigation: NavigationProp<ParamListBase>;
  branchCount: number;
  /** Sin padding exterior (dentro de PostLoginHubOwnerChrome). */
  embedded?: boolean;
  identityNavigationContext?: OrganizationIdentityNavigationContext;
};

export function OrganizationHubPill({
  navigation,
  branchCount,
  embedded = false,
  identityNavigationContext = 'shell',
}: Props) {
  const { entitlements } = usePlanEntitlements();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const { canOpenOrganizationMenu } = useOrganizationSwitcherAccess();
  const canEditOrg = useModulePermission('Organization', 'edit');
  const isOwner = entitlements?.is_owner === true;
  const canManageIdentity = isOwner || canEditOrg;
  const [sheetOpen, setSheetOpen] = useState(false);

  const orgName = getOrganizationDisplayName(entitlements) ?? 'Tu negocio';
  const rubro = resolveOrganizationRubroDisplayLabel(businessProfile) ?? 'Negocio';
  const meta = `${branchCount} sucursal${branchCount === 1 ? '' : 'es'} · ${rubro}`;
  const orgLogoUrl = businessProfile?.organization_logo_url ?? null;
  const showMarkHint = canManageIdentity && !orgLogoUrl;

  const openIdentity = useCallback(() => {
    if (!canManageIdentity) return;
    navigateToOrganizationIdentity(navigation, identityNavigationContext, { focusSection: 'logo' });
  }, [canManageIdentity, identityNavigationContext, navigation]);

  const markNode = (
    <View style={[styles.mark, showMarkHint && styles.markEmpty]}>
      {orgLogoUrl ? (
        <Image source={{ uri: orgLogoUrl }} style={styles.markImage} resizeMode="cover" />
      ) : (
        <Text style={styles.markText}>{orgName.trim().charAt(0).toUpperCase() || 'N'}</Text>
      )}
    </View>
  );

  const textBlock = (
    <View style={styles.textWrap}>
      <Text style={styles.name} numberOfLines={1}>
        {orgName}
      </Text>
      <Text style={styles.meta} numberOfLines={1}>
        {meta}
      </Text>
    </View>
  );

  return (
    <>
      <View style={[styles.wrap, embedded && styles.wrapEmbedded]} testID="org-switcher.hub-pill">
        <View style={styles.pill}>
          {canManageIdentity ? (
            <TouchableOpacity
              onPress={openIdentity}
              accessibilityRole="button"
              accessibilityLabel={
                orgLogoUrl ? 'Logo del negocio, editar' : 'Logo del negocio, subir imagen'
              }
              testID="org-switcher.identity-mark"
              hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
            >
              {markNode}
            </TouchableOpacity>
          ) : (
            markNode
          )}

          {canOpenOrganizationMenu ? (
            <TouchableOpacity
              style={styles.menuHit}
              onPress={() => setSheetOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={`Tus negocios: ${orgName}`}
              testID="org-switcher.pill"
            >
              {textBlock}
              <Ionicons name="chevron-down" size={16} color={ticketColors.ink3} />
            </TouchableOpacity>
          ) : (
            <View style={styles.menuHit}>{textBlock}</View>
          )}
        </View>
      </View>
      <OrganizationSwitcherSheet
        visible={sheetOpen}
        onClose={() => setSheetOpen(false)}
        navigation={navigation}
      />
    </>
  );
}

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    backgroundColor: ticketColors.bg,
  },
  wrapEmbedded: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: 'transparent',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  menuHit: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  mark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  markEmpty: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  markImage: {
    width: 36,
    height: 36,
  },
  markText: {
    ...typography.body,
    fontWeight: '800',
    color: colors.surface,
  },
  textWrap: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  name: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  meta: {
    ...typography.caption,
    color: colors.textSecondary,
  },
});
